import { Row, Spin } from "antd";
import Select from "../../../../components/generic/Select";
import CostEstimator from "./CostEstimator";
import PriceCard from "./PriceCard";
import {
  CURRENCY_OPTIONS,
  CURRENCY_SYMBOL,
  getPriceForCurrency,
  getSubscriptionDetails,
} from "../helper";
import { useEffect, useMemo, useState } from "react";
import { parseError } from "../../../../utils/shared";
import httpClient from "../../../../api/httpClient";
import { StyledPricing } from "./styles";
import { useSelector } from "react-redux";
import { selectCurrentPlan } from "../../../../store/user/selectors";

const Pricing = ({ closeModal = null, isCanceled = false }) => {
  const currentPlan = useSelector(selectCurrentPlan);
  const [teamMembers, setTeamMembers] = useState(5);
  const [currency, setCurrency] = useState("GBP");
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [{ perMemberPrice, plans }, setProducts] = useState({
    perMemberPrice: null,
    plans: [],
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        //@ts-ignore
        const { products } = await httpClient.get("paddle/products");

        const perSeat = products?.find((p) => p?.name === "Kelu Per Seat");

        const filteredPlans = (
          products?.filter((p) => p?.name !== "Kelu Per Seat") || []
        ).map((e) => ({ ...e, price: e?.prices?.[0] || {} }));

        setProducts({
          perMemberPrice: { ...perSeat, price: perSeat?.prices?.[0] },
          plans: filteredPlans,
        });

        if (!currentPlan) {
          setSelectedPlanId(filteredPlans?.[0]?.id || null);
        } else {
          const { perSeat: currentPerSeat, selectedPlan: currentSelectedPlan } =
            getSubscriptionDetails(currentPlan);

          const quantity = currentPerSeat?.quantity;
          const planId = currentSelectedPlan?.id;

          if (quantity) setTeamMembers(quantity);

          if (planId) setSelectedPlanId(planId);
        }
      } catch (error) {
        parseError(error, true);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const { currencySymbol, selectedPlan, ...pricingDetails } = useMemo(() => {
    if (!selectedPlanId || !perMemberPrice)
      return {
        currencySymbol: CURRENCY_SYMBOL[currency],
      };

    const selectedPlanData = plans.find((p) => p.id === selectedPlanId);

    const basePlanPrice = getPriceForCurrency(selectedPlanData.price, currency);

    const basePlanAmount = parseFloat(basePlanPrice.amount) / 100;
    const memberAmount =
      parseFloat(getPriceForCurrency(perMemberPrice.price, currency).amount) /
      100;

    const totalMembersCost = memberAmount * teamMembers;
    const monthlyCost = basePlanAmount + totalMembersCost;

    return {
      currencySymbol: CURRENCY_SYMBOL[currency],
      selectedPlan: selectedPlanData,
      basePlanAmount,
      memberAmount,
      totalMembersCost,
      monthlyCost,
    };
  }, [selectedPlanId, currency, teamMembers, perMemberPrice, plans]);

  return (
    <StyledPricing>
      {loading ? (
        <div className="h-100 w-100 flex-center loading">
          <Spin />
        </div>
      ) : (
        <div className="pricing-container">
          {/* Header */}
          <div className="flex justify-space-between">
            <div className="section-header">
              <h1 className="header-title">Simple, Transparent Pricing</h1>
              <p className="header-subtitle">
                Choose the perfect plan for your team. Upgrade or downgrade
                anytime.
              </p>
            </div>
            <div>
              <Select
                value={currency}
                onChange={setCurrency}
                style={{ width: "180px", marginRight: "10px" }}
                options={CURRENCY_OPTIONS}
              />
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="pricing-cards-wrapper">
            <Row gutter={[24, 24]}>
              {plans.map((plan, index) => (
                <PriceCard
                  key={index}
                  plan={plan}
                  memberAmount={pricingDetails.memberAmount}
                  currencySymbol={currencySymbol}
                  currency={currency}
                  setSelectedPlanId={setSelectedPlanId}
                  selectedPlanId={selectedPlanId}
                />
              ))}
            </Row>
          </div>

          <CostEstimator
            plans={plans}
            pricingDetails={pricingDetails}
            currencySymbol={currencySymbol}
            selectedPlan={selectedPlan}
            perMemberPrice={perMemberPrice}
            selectedPlanId={selectedPlanId}
            setSelectedPlanId={setSelectedPlanId}
            teamMembers={teamMembers}
            setTeamMembers={setTeamMembers}
            closeModal={closeModal}
            isCanceled={isCanceled}
            currentPlan={currentPlan}
          />
        </div>
      )}
    </StyledPricing>
  );
};

export default Pricing;
